import { Observable, concat, defer, map, of } from 'rxjs';
import { CacheService } from '../../services/cache.service';
import {
  latLngToTile,
  tileBoundsAtZoom,
} from './spatial/tile-math';
import { MapViewport, mapViewport } from './spatial/map-viewport';

export type { MapBounds, MapViewport } from './spatial/map-viewport';

export interface MapTilePoint {
  readonly lat: number;
  readonly lng: number;
}

export interface MapServerCluster {
  readonly tileX: number;
  readonly tileY: number;
  readonly zoom: number;
  readonly lat: number;
  readonly lng: number;
  readonly count: number;
}

export interface MapTileResponse<TPoint extends MapTilePoint> {
  readonly points: readonly TPoint[];
  readonly clusters?: readonly MapServerCluster[];
}

export interface MapTotalResponse {
  readonly total: number;
}

export const MAP_CLUSTER_MODE_SWITCH_ZOOM = 16;

export interface TileCoordinate {
  readonly zoom: number;
  readonly tileX: number;
  readonly tileY: number;
}

interface TileEntry<TPoint> {
  readonly points: readonly TPoint[];
  readonly clusters: readonly MapServerCluster[];
}

/**
 * Shared client cache for viewport-scoped map data.
 *
 * A cache instance belongs to one concrete map feature. Business filters are part of
 * the key; geometry math, missing-tile requests, request de-duplication and
 * LRU eviction stay identical for Customer, Lead and future dynamic features.
 */
export class MapTileCache<TPoint extends MapTilePoint> {
  constructor(
    private readonly cache: CacheService,
    private readonly namespace: string,
    private readonly pointId: (point: TPoint) => number | string,
    private readonly capacity = 2000,
  ) {}

  filter(
    viewport: MapViewport,
    filterKey: string,
    fetch: (viewport: MapViewport) => Observable<MapTileResponse<TPoint>>,
  ): Observable<MapTileResponse<TPoint>> {
    return defer(() => {
      const visibleTiles = tilesForViewport(viewport);
      const missing = visibleTiles.filter(tile =>
        !this.cache.has(this.namespace, this.tileKey(filterKey, tile)));
      const cached = this.snapshot(filterKey, visibleTiles);

      if (missing.length === 0) return of(cached);

      const requestedTiles = tileRectangle(missing);
      const requestViewport = viewportForTiles(requestedTiles);
      const requestKey = requestedTiles
        .map(tile => this.tileKey(filterKey, tile))
        .join('|');
      const request = this.cache.load(this.namespace, requestKey, () =>
        fetch(requestViewport).pipe(map(response => {
          this.storeResponse(filterKey, requestedTiles, response);
          return response;
        })), { retain: false });

      const refreshed = request.pipe(map(() => {
        // Build this response before enforcing the cache capacity. A viewport
        // can span more tiles than the cache retains; eviction must only affect
        // future reuse, never discard points from the response that completed.
        const result = this.snapshot(filterKey, visibleTiles);
        this.evictOverflow();
        return result;
      }));

      return cached.points.length > 0 || (cached.clusters?.length ?? 0) > 0
        ? concat(of(cached), refreshed)
        : refreshed;
    });
  }

  clear(): void {
    this.cache.clear(this.namespace);
  }

  private snapshot(
    filterKey: string,
    tiles: readonly TileCoordinate[],
  ): MapTileResponse<TPoint> {
    const points = this.uniquePoints(this.pointsForTiles(filterKey, tiles));
    const clusters = this.clustersForTiles(filterKey, tiles);
    return {
      points,
      clusters,
    };
  }

  private pointsForTiles(
    filterKey: string,
    tiles: readonly TileCoordinate[],
  ): readonly TPoint[] {
    return tiles.flatMap(tile => {
      const key = this.tileKey(filterKey, tile);
      const entry = this.cache.get<TileEntry<TPoint>>(this.namespace, key);
      if (!entry) return [];

      return entry.points;
    });
  }

  private storeResponse(
    filterKey: string,
    requestedTiles: readonly TileCoordinate[],
    response: MapTileResponse<TPoint>,
  ): void {
    const requested = new Map(requestedTiles.map(tile => [
      tileIdentity(tile),
      tile,
    ]));
    const pointsByTile = new Map<string, TPoint[]>();
    const clustersByTile = new Map<string, MapServerCluster[]>();

    for (const point of response.points) {
      const coordinate = latLngToTile(point.lat, point.lng,
        requestedTiles[0].zoom);
      const identity = tileIdentity({
        zoom: requestedTiles[0].zoom,
        ...coordinate,
      });
      if (!requested.has(identity)) continue;
      const bucket = pointsByTile.get(identity) ?? [];
      bucket.push(point);
      pointsByTile.set(identity, bucket);
    }

    for (const cluster of response.clusters ?? []) {
      const identity = tileIdentity(cluster);
      if (!requested.has(identity)) continue;
      const bucket = clustersByTile.get(identity) ?? [];
      bucket.push(cluster);
      clustersByTile.set(identity, bucket);
    }

    for (const tile of requestedTiles) {
      const key = this.tileKey(filterKey, tile);
      const identity = tileIdentity(tile);
      this.cache.set(this.namespace, key, {
        points: pointsByTile.get(identity) ?? [],
        clusters: clustersByTile.get(identity) ?? [],
      });
    }
  }

  private clustersForTiles(
    filterKey: string,
    tiles: readonly TileCoordinate[],
  ): readonly MapServerCluster[] {
    return tiles.flatMap(tile => {
      const key = this.tileKey(filterKey, tile);
      const entry = this.cache.get<TileEntry<TPoint>>(this.namespace, key);
      if (!entry) return [];

      return entry.clusters;
    });
  }

  private evictOverflow(): void {
    this.cache.trim(this.namespace, this.capacity);
  }

  private tileKey(filterKey: string, tile: TileCoordinate): string {
    return `${filterKey}:${tileIdentity(tile)}`;
  }

  private uniquePoints(points: readonly TPoint[]): readonly TPoint[] {
    const seen = new Set<number | string>();
    return points.filter(point => {
      const id = this.pointId(point);
      if (seen.has(id)) return false;
      seen.add(id);
      return true;
    });
  }
}

export function tilesForViewport(viewport: MapViewport): readonly TileCoordinate[] {
  const zoom = Math.max(0, Math.floor(viewport.zoom));
  const northWest = latLngToTile(viewport.north, viewport.west, zoom);
  const southEast = latLngToTile(viewport.south, viewport.east, zoom);
  const tiles: TileCoordinate[] = [];

  for (let tileX = northWest.tileX; tileX <= southEast.tileX; tileX++) {
    for (let tileY = northWest.tileY; tileY <= southEast.tileY; tileY++) {
      tiles.push({ zoom, tileX, tileY });
    }
  }
  return tiles;
}

export function tileRectangle(
  tiles: readonly TileCoordinate[],
): readonly TileCoordinate[] {
  const zoom = tiles[0].zoom;
  const minX = Math.min(...tiles.map(tile => tile.tileX));
  const maxX = Math.max(...tiles.map(tile => tile.tileX));
  const minY = Math.min(...tiles.map(tile => tile.tileY));
  const maxY = Math.max(...tiles.map(tile => tile.tileY));
  const result: TileCoordinate[] = [];

  for (let tileX = minX; tileX <= maxX; tileX++) {
    for (let tileY = minY; tileY <= maxY; tileY++) {
      result.push({ zoom, tileX, tileY });
    }
  }
  return result;
}

export function viewportForTiles(tiles: readonly TileCoordinate[]): MapViewport {
  const bounds = tiles
    .map(tile => tileBoundsAtZoom(tile.tileX, tile.tileY, tile.zoom))
    .reduce((combined, tileBounds) => combined.extend(tileBounds));
  return mapViewport(bounds, tiles[0].zoom);
}

function tileIdentity(tile: TileCoordinate): string {
  return `${tile.zoom}/${tile.tileX}/${tile.tileY}`;
}
